import type { Channel, Program } from "@hybrid-iptv/types";
import type { ProviderConfig } from "../settings/settingsStore.js";
import { useSettings } from "../settings/settingsStore.js";
import { api, runsOnDevice } from "../api/client.js";
import { extraGuideUrls, guideTuning } from "./guideFeeds.js";

/** Thin API wrappers — same-origin calls to services/api, never the provider. */

export interface EpgResponse {
  sourceId: string;
  channels: { id: string; displayName: string }[];
  programs: import("@hybrid-iptv/types").Program[];
  /** XMLTV channelId → original catalog names the client requested. An array
   * because two catalog channels (e.g. "USA ESPN" and "USA ESPN UHD") can
   * resolve to the same feed channel — both get that channel's programs. */
  nameMapping?: Record<string, string[]>;
  cached: boolean;
  fetchedAt: string;
}

/** The active provider's sourceId (its stable per-provider id). */
export function activeProviderSourceId(): string {
  const active = useSettings.getState().activeProvider;
  return active?.id ?? "default";
}

/** Reactive hook — the active provider's sourceId, rerenders on switch. */
export function useActiveProviderSourceId(): string {
  const active = useSettings((s) => s.activeProvider);
  return active?.id ?? "default";
}

/**
 * Build the provider-credentials body sent with every catalog/EPG request so
 * the server fetches from the user's own provider (not an env-configured one).
 * Returns an empty object when no credentials are saved — the server falls
 * back to the env provider for local dev.
 */
export function providerBody(provider?: ProviderConfig): Record<string, unknown> {
  const config = provider ?? useSettings.getState().activeProvider;
  if (!config) return {};
  if (!config.isValid && !config.xtreamBaseUrl && !config.m3uUrl) return {};
  if (config.kind === "m3u" && config.m3uUrl) {
    // The viewer's own guide feed wins over the one the playlist names
    // (resolveEpgUrl honours an explicit epgUrl on the server and the device).
    return { kind: "m3u", m3uUrl: config.m3uUrl, ...(config.epgUrl ? { epgUrl: config.epgUrl } : {}) };
  }
  if (config.kind === "xtream" && config.xtreamBaseUrl && config.xtreamUsername) {
    return {
      kind: "xtream",
      xtreamBaseUrl: config.xtreamBaseUrl,
      xtreamUsername: config.xtreamUsername,
      xtreamPassword: config.xtreamPassword,
    };
  }
  return {};
}

/** `force`: Settings' "Refresh now" — the server downloads again whatever its copy's age. */
export async function fetchChannels(sourceId: string, force = false): Promise<Channel[]> {
  return api.post<Channel[]>(`/api/sources/${sourceId}/live-streams`, { ...providerBody(), ...(force ? { force: true } : {}) });
}

/**
 * Fetch a SPECIFIC provider's channel catalog by sending THAT provider's
 * credentials (not the active one's). Used by the Section 16 mirror picker,
 * which must list another user-owned provider to link a fallback. One user-
 * initiated fetch at a time — never a boot-time scan, honoring the connection
 * budget.
 */
export async function fetchProviderChannels(sourceId: string, provider: ProviderConfig): Promise<Channel[]> {
  return api.post<Channel[]>(`/api/sources/${sourceId}/live-streams`, providerBody(provider));
}

export async function fetchEpg(sourceId: string, channelIds: string[], windowHours?: number): Promise<EpgResponse> {
  return api.post<EpgResponse>(`/api/sources/${sourceId}/epg/xmltv`, {
    ...providerBody(),
    channelIds,
    ...(windowHours ? { windowHours } : {}),
  });
}

export async function fetchEpgByName(sourceId: string, channelNames: string[], windowHours?: number): Promise<EpgResponse> {
  return api.post<EpgResponse>(`/api/sources/${sourceId}/epg/xmltv`, {
    ...providerBody(),
    channelNames,
    ...(windowHours ? { windowHours } : {}),
  });
}

/**
 * The guide for some channels only (Phase 3 step 2a, the APK's native guide,
 * api/deviceApi.ts deviceGuideFor). `channelNames` is every channel, as
 * fetchEpgByName sends it, so the name match is the same one; programmes come
 * back for `onlyNames` alone. `details: false` leaves out subtitles and
 * descriptions. Anywhere else it is the whole guide, as before.
 */
export async function fetchEpgFor(
  sourceId: string,
  channelNames: string[],
  onlyNames: readonly string[],
  windowHours: number,
  details = true,
): Promise<EpgResponse> {
  if (runsOnDevice()) {
    const { deviceGuideFor } = await import("../api/deviceApi.js");
    const answer = await deviceGuideFor(
      sourceId,
      providerBody(),
      channelNames,
      onlyNames,
      windowHours,
      details,
      extraGuideUrls(sourceId),
      guideTuning(sourceId) ?? undefined,
    );
    // The same object the route would have sent as JSON.
    if (answer) return answer as unknown as EpgResponse;
  }
  return fetchEpgByName(sourceId, channelNames, windowHours);
}

/**
 * One channel's archive listing, for the Catch-up page (Section 16 step 4).
 *
 * Separate from the guide fetch on purpose: the XMLTV window is trimmed to a
 * few hours back, while the panel's per-channel table carries a week of past
 * listings. `streamId` is the provider's own numeric id (read out of the
 * channel's live URL); `channelId` is ours, so the programmes come back already
 * keyed to the catalog.
 */
export async function fetchChannelArchive(
  sourceId: string,
  streamId: string,
  channelId: string,
): Promise<{ programs: Program[] }> {
  return api.post<{ programs: Program[] }>(`/api/sources/${sourceId}/epg/archive`, {
    ...providerBody(),
    streamId,
    channelId,
  });
}

/** Send XMLTV channel IDs directly — the provider's epg_channel_id values. */
export async function fetchEpgByIds(sourceId: string, channelIds: string[], windowHours?: number): Promise<EpgResponse> {
  return api.post<EpgResponse>(`/api/sources/${sourceId}/epg/xmltv`, {
    ...providerBody(),
    channelIds,
    ...(windowHours ? { windowHours } : {}),
  });
}
